import { FormField, GroupInputField } from "../../../FormEngine/types";
import UserService from "../../../../../services/userService";
import { toast } from "react-toastify";
import { FormState } from "../../FormContext/types";
import { SleepMedicineHistoryData } from "src/revamp/types/PatientFormTypes";
import { z } from "zod";

/**
 * Sleep Medicine History form schema
 * 
 * This schema defines the fields for the Sleep Medicine History form
 * using the FormEngine component.
 */
export const sleepMedicineHistorySchema: FormField[] = [
	// Past Sleep Study
	{
		type: "group-input" as const,
		name: "pastSleepStudy",
		title: "Past Sleep Study",
		description: "Have you had a sleep study in the past?",
		group: [
			{
				type: "radio",
				name: 'pastSleepStudy.response',
				fieldProps: {
					options: [
						{ label: "Yes", value: true },
						{ label: "No", value: false },
					],
					sx: { flexDirection: "row", gap: 1 },
				},
			},
			{
				type: "input",
				name: 'pastSleepStudy.description',
				label: "Description",
				conditional: {
					label: 'pastSleepStudy.response',
					values: [true],
				},
				rule: z.string(),
			},
			{ // document upload
				type: "file-input",
				name: 'pastSleepStudy.document',
				label: "Document",
				conditional: {
					label: 'pastSleepStudy.response',
					values: [true],
				},
				fieldProps: {
					dragAndDrop: true,
				}
			},
		],
		fieldProps: {
			direction: "column",
		}
	},
	
	// Using PAP
	{
		type: "group-input" as const,
		name: "onPAP",
		title: "Using PAP",
		description: "Are you currently using a CPAP/BiPAP machine?",
		group: [
			{
				type: "radio",
				name: 'isOnPAP',
				fieldProps: {
					options: [
						{ label: "Yes", value: true },
						{ label: "No", value: false },
					],
					sx: { flexDirection: "row", gap: 1 },
				},
			},
			{
				type: "input",
				name: 'papVendor',
				label: "PAP Vendor",
				fieldProps: {
					helperText: "Please enter the vendor of your PAP machine",
				},
				conditional: {
					label: 'isOnPAP',
					values: [true],
				},
				rule: z.string().min(1, "PAP Vendor is required"),
			},
		],
		fieldProps: {
			direction: "column",
		}
	},
	
	// Drowsiness Accident
	{
		type: "group-input" as const,
		name: "drowsinessAccident",
		title: "Drowsiness Accident",
		description: "Have you ever had an accident or near accident due to excessive drowsiness?",
		group: [
			{
				type: "radio",
				name: 'drowsinessAccident.response',
				fieldProps: {
					options: [
						{ label: "Yes", value: true },
						{ label: "No", value: false },
					],
					sx: { flexDirection: "row", gap: 1 },
				},
			},
			{
				type: "input",
				name: 'drowsinessAccident.description',
				label: "Description",
				conditional: {
					label: 'drowsinessAccident.response',
					values: [true],
				},
				rule: z.string().min(1, "Description is required"),
			},
		],
		fieldProps: {
			direction: "column",
		}
	},
];

export const sleepStudyFileUpload = async (data: SleepMedicineHistoryData, formState: FormState) => {
	const file = data.pastSleepStudy.document;
	if (!file || typeof file === 'string') return;

	// Create form data for file upload
	const formData = new FormData();
	formData.append('file', file);

	// Upload file to AWS
	const uploadResponse = await UserService.uploadFile(formData);
	const uploadedFileName = uploadResponse?.data?.file;

	if (!uploadedFileName) throw new Error('File upload failed');

	// Create form record
	await UserService.uploadForm({
		category: 'HST',
		name: `Past Sleep Study - ${uploadedFileName}`,
		key: uploadedFileName,
		patientId: formState.patientId,
		usedFor: 'Patient Onboarding',
		attachFile: true
	}).then(() => {
		toast.success('File uploaded successfully');
		data.pastSleepStudy.document = uploadedFileName;
	}).catch(() => {
		toast.error('File upload failed');
	});
}

export default sleepMedicineHistorySchema;